import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import useUpdateShop from "@babackapp/private-api-client/client/use-update-shop";
import useUpdateShopPreferences from "@babackapp/private-api-client/client/use-update-shop-preferences";
import SettingsFormContainer, {
  SettingsFormHeader,
  SettingsFormPageButtonBar,
  SettingsFormSectionHeader,
} from "@babackapp/react-hook-forms-components/form-page";
import useTranslation from "@babackapp/translations/use-translation";
import { Form } from "@babackapp/ui/components/form";
import { useForm } from "react-hook-form";
import DuplicateShopSettingsDialog from "../duplicate-shop-settings/duplicate-shop-settings-dialog";
import ReturnToStoresCard from "../return-to-store/return-to-store-card";
import TeamMembersCard from "../team-members/team-members-card";
import WorkflowLogisiticianCard from "../workflow-logisitican/workflow-logistician-card";
import ShopSettingsContactForm from "./shop-settings-contact-form";
import ShopSettingsInformationForm from "./shop-settings-information-form";
import { ShopSettingsFormValues } from "./types";
import { DAY_MS } from "@/lib/utils/date.ts";

function ShopSettingsForm() {
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateShop } = useUpdateShop(shop.id);
  const { mutateAsync: updateShopPreferences } = useUpdateShopPreferences(
    shop.id,
  );

  const form = useForm<ShopSettingsFormValues>({
    defaultValues: {
      name: shop.name,
      vatNumber: shop.vatNumber,
      email: shop.email,
      phone: shop.phone,
      /**
       * From milliseconds to days
       */
      timeDelay: shop.timeDelay / DAY_MS,
      returnLabelDownloadDelayDays:
        shop.preferences?.returnLabelDownloadDelayDays,
      returnCancellationDelayMonths:
        shop.preferences?.returnCancellationDelayMonths ?? -1,
      giftCode: {
        activated: shop.giftCode.activated || false,
      },
      marketConversionRates: {
        enabled: shop.preferences?.marketConversionRates?.enabled ?? false,
        rates: shop.preferences?.marketConversionRates?.rates ?? [],
      },
      // oneResolutionPerReturn: shop.oneResolutionPerReturn || false,
      logoId: {
        id: shop.logoId || "",
        url: shop.logoUrl,
      },
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await Promise.all([
      updateShop({
        email: values.email,
        name: values.name,
        phone: values.phone,
        vatNumber: values.vatNumber,
        timeDelay: values.timeDelay * DAY_MS,
        giftCode: {
          activated: values.giftCode.activated,
        },
      }),
      updateShopPreferences({
        returnLabelDownloadDelayDays: values?.returnLabelDownloadDelayDays,
        returnCancellationDelayMonths: values?.returnCancellationDelayMonths,
        marketConversionRates: {
          enabled: values.marketConversionRates.enabled,
          rates: values.marketConversionRates.rates.filter(
            (r) => r.currencyCode !== "",
          ),
        },
      }),
    ]);

    form.reset({
      ...values,
      marketConversionRates: {
        ...values.marketConversionRates,
        // Drop rows that were added but never filled in (filtered out before the
        // API call) so they don't reappear after a successful save.
        rates: values.marketConversionRates.rates.filter(
          (r) => r.currencyCode !== "",
        ),
      },
    });
  });

  return (
    <div className="w-full pb-2 flex justify-start relative">
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col w-full">
          <div className="flex flex-col gap-8">
            <ShopSettingsInformationForm />
            <ShopSettingsContactForm />
          </div>
          <SettingsFormPageButtonBar />
        </form>
      </Form>
    </div>
  );
}

export default function ShopSettings() {
  const { data: shop } = useCurrentShopSuspense();
  const isSuperAdmin = useViewerIsSuperAdmin();
  const t = useTranslation();

  return (
    <SettingsFormContainer
      navigation={[
        {
          title: t("settings.shop.sections.basic.title"),
          id: "information",
        },
        {
          title: t("settings.shop.sections.contact.title"),
          id: "contact",
        },
        {
          title: t("settings.config.workflowLogistician.name"),
          id: "workflow-logistician",
        },
        {
          title: t("settings.team.title"),
          id: "team",
        },
      ]}
    >
      <div className="flex items-start justify-between gap-4">
        <SettingsFormHeader
          title={t("settings.shop.title")}
          description={t("settings.shop.subtitle")}
        />
        {isSuperAdmin && (
          <div className="pt-1 shrink-0">
            <DuplicateShopSettingsDialog
              sourceShopId={shop.id}
              sourceShopName={shop.name}
              sourcePlanReturnToStore={shop.plan?.returnToStore}
            />
          </div>
        )}
      </div>
      <ShopSettingsForm />
      <SettingsFormSectionHeader
        title={t("settings.config.workflowLogistician.name")}
        id="workflow-logistician"
      />
      <WorkflowLogisiticianCard />

      {shop.plan.returnToStore && (
        <>
          <SettingsFormSectionHeader
            title={t("settings.shop.physic.title")}
            id="stores"
          />
          <ReturnToStoresCard />
        </>
      )}

      <SettingsFormSectionHeader title={t("settings.team.title")} id="team" />
      <TeamMembersCard />
    </SettingsFormContainer>
  );
}
